import React, { useCallback, useEffect, useRef, useState } from "react";

// --- Firmware update (OTA) UUIDs ---
const OTA_SERVICE_UUID = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
const OTA_CTRL_UUID = "6e400010-b5a3-f393-e0a9-e50e24dcca9e";
const OTA_DATA_UUID = "6e400011-b5a3-f393-e0a9-e50e24dcca9e";
const OTA_STAT_UUID = "6e400012-b5a3-f393-e0a9-e50e24dcca9e";

// --- Provisioning / DEVSET UUIDs ---
const DEVSET_SERVICE_UUID = "8f0c0001-5a1f-4b6a-9b12-3f1c8b2d4c10";
const DEVSET_INFO_UUID = "8f0c0002-5a1f-4b6a-9b12-3f1c8b2d4c10";   // NEW (read)
const DEVSET_WRITE_UUID = "8f0c0003-5a1f-4b6a-9b12-3f1c8b2d4c10";  // write ID=...
const DEVSET_STATUS_UUID = "8f0c0004-5a1f-4b6a-9b12-3f1c8b2d4c10"; // notify OK/ERR

const LH_PREFIX = "LH";
const UNPROVISIONED_NAME = "LH-UNPROVISIONED";

// Local firmware path (place firmware.bin in /public/firmware/firmware.bin)
const FIRMWARE_URL = "/firmware/lyeonsFW_v1_0_3.bin";
const DEV_FIRMWARE_URL = "/firmware/lyeonsFW_v1_0_3.bin";

// Set this to the minimum firmware ID that counts as “up to date”
const TARGET_FW_ID = 10003; // <— change as needed
const DEV_TARGET_FW_ID = 10003;

// Local storage keys (optional)
const LS_KEYS = {
  lastSerial: "lyeons.update.lastSerial",
  pktsz: "lyeons.update.pktsz",
  wrr: "lyeons.update.wrr",
};

function supportsWebBluetooth() {
  return typeof navigator !== "undefined" && !!navigator.bluetooth;
}

function hasDevFirmwareParam() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("dev");
}

function sleep(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) throw new Error(`sleep(ms) got non-number: ${String(ms)}`);
  const delay = Math.max(0, Math.floor(n));
  return new Promise((r) => setTimeout(r, delay));
}


function u32leBytes(n) {
  const x = n >>> 0;
  return new Uint8Array([x & 0xff, (x >>> 8) & 0xff, (x >>> 16) & 0xff, (x >>> 24) & 0xff]);
}

function concatBytes(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function bytesFromAscii(str) {
  return new TextEncoder().encode(str);
}

async function disconnectDevice(device) {
  try {
    if (device?.gatt?.connected) device.gatt.disconnect();
  } catch {}
}

async function debugSleep(ms, tag = "") {
  console.log(`[SLEEP] requested ms=`, ms, ` type=${typeof ms} tag=${tag}`);
  const t0 = performance.now();
  await sleep(ms);
  console.log(`[SLEEP] actual ${(performance.now() - t0).toFixed(0)}ms tag=${tag}`);
}

async function connectGatt(device, onDisconnected) {
  if (!device) throw new Error("No device selected");
  if (!device.gatt) throw new Error("No GATT on device");
  if (onDisconnected) device.addEventListener("gattserverdisconnected", onDisconnected);
  return await device.gatt.connect();
}

async function getCharacteristicByUuid(server, charUuid) {
  const services = await server.getPrimaryServices();
  const target = charUuid.toLowerCase();
  for (const svc of services) {
    try {
      const chars = await svc.getCharacteristics();
      for (const ch of chars) {
        if ((ch.uuid || "").toLowerCase() === target) return ch;
      }
    } catch {}
  }
  throw new Error(`Characteristic not found: ${charUuid}`);
}

async function getCharacteristic(server, serviceUuidOrNull, charUuid) {
  if (serviceUuidOrNull) {
    const svc = await server.getPrimaryService(serviceUuidOrNull);
    return await svc.getCharacteristic(charUuid);
  }
  return await getCharacteristicByUuid(server, charUuid);
}

async function writeMaybeWithResponse(ch, bytes, withResponse) {
  if (withResponse && ch.writeValueWithResponse) return ch.writeValueWithResponse(bytes);
  if (!withResponse && ch.writeValueWithoutResponse) return ch.writeValueWithoutResponse(bytes);
  return ch.writeValue(bytes);
}

async function startNotifications(ch, onBytes) {
  await ch.startNotifications();
  const handler = (e) => {
    const dv = e?.target?.value;
    if (!dv) return;
    const u8 = new Uint8Array(dv.buffer, dv.byteOffset, dv.byteLength);
    onBytes(u8);
  };
  ch.addEventListener("characteristicvaluechanged", handler);
  return async () => {
    try {
      ch.removeEventListener("characteristicvaluechanged", handler);
      await ch.stopNotifications();
    } catch {}
  };
}

/**
 * Returns true if DEVSET service exists on this device.
 */
async function hasDevsetService(server) {
  try {
    await server.getPrimaryService(DEVSET_SERVICE_UUID);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads DEVSET info JSON from 8f0c0002...
 * Expected JSON like:
 * {"device_id":"...","fw":"...","fw_id":123,"provisioned":1}
 */
async function readDevsetInfo(server) {
  const ch = await getCharacteristic(server, DEVSET_SERVICE_UUID, DEVSET_INFO_UUID);
  const dv = await ch.readValue();
  const txt = new TextDecoder("utf-8").decode(dv.buffer).trim();
  if (!txt) throw new Error("Empty DEVSET info");
  let obj;
  try {
    obj = JSON.parse(txt);
  } catch {
    throw new Error("Could not parse DEVSET info");
  }
  return {
    device_id: String(obj.device_id ?? ""),
    fw: String(obj.fw ?? ""),
    fw_id: Number(obj.fw_id ?? -1),
    provisioned: Number(obj.provisioned ?? 0),
    raw: txt,
  };
}

/**
 * Firmware update upload: STAR/DATA/FINI + STAT
 * UX behaviour:
 * - Progress is capped at 95% while transferring
 * - When OK:reboot arrives, keep at 95% and show "Restarting..."
 * - When function completes, set progress to 100%
 */
async function firmwareUpdateUpload({
  server,
  firmwareBytes,
  wrr,
  initialChunkSize,
  onUiProgressPct,
  onUiStatus,
  abortSignal,
}) {
  if (!server?.connected) throw new Error("Not connected to device");
  if (!firmwareBytes?.length) throw new Error("Firmware file is missing");

  const ctrl = await getCharacteristic(server, OTA_SERVICE_UUID, OTA_CTRL_UUID);
  const data = await getCharacteristic(server, OTA_SERVICE_UUID, OTA_DATA_UUID);
  const stat = await getCharacteristic(server, OTA_SERVICE_UUID, OTA_STAT_UUID);

  const paceMs = /Mac/i.test(navigator.userAgent) ? 10 : 10;

  let lastStat = "";
  let statResolvers = [];

  let done = false;
  let sawRebootMsg = false;

  let creepTimer = null;
  const startCreep = () => {
    let p = 95;
    creepTimer = setInterval(() => {
      p = Math.min(97, p + 1);
      onUiProgressPct?.(p);
      if (p >= 97) {
        clearInterval(creepTimer);
        creepTimer = null;
      }
    }, 6000);
  };
  const stopCreep = () => {
    if (creepTimer) {
      clearInterval(creepTimer);
      creepTimer = null;
    }
  };

  const stopStat = await startNotifications(stat, (u8) => {
    const s = new TextDecoder("utf-8").decode(u8).trim();
    if (!s) return;

    console.log("[STAT]", s);
    lastStat = s;

    if (s.startsWith("OK:reboot")) {
      sawRebootMsg = true;
      stopCreep();
      onUiStatus?.("Restarting the device…");
      onUiProgressPct?.(98);
      done = true;
      return;
    }

    if (s.startsWith("ERR:")) {
      stopCreep();
      done = true;
      onUiStatus?.("Something went wrong during the update.");
      return;
    }

    statResolvers.forEach((r) => r(s));
    statResolvers = [];

  });

  const maybeAbort = () => {
    if (abortSignal?.aborted) throw new Error("Aborted");
  };

  try {
    onUiStatus?.("Preparing the update…");
    onUiProgressPct?.(1);

    console.log("[OTA] CTRL props:", {
      write: !!ctrl.properties?.write,
      writeWithoutResponse: !!ctrl.properties?.writeWithoutResponse,
      authenticatedSignedWrites: !!ctrl.properties?.authenticatedSignedWrites,
    });

    const total = firmwareBytes.length >>> 0;
    const star = concatBytes(bytesFromAscii("STAR"), u32leBytes(total));
    await debugSleep(5000, "before STAR");
    await writeMaybeWithResponse(ctrl, star, false);
    await debugSleep(5000, "after STAR");
    onUiStatus?.("Updating firmware…");

    let pay = Math.max(20, Number(initialChunkSize || 80));
    let sent = 0;
    let lastShown = -1;

    const updateUiPct = () => {
      const rawPct = Math.floor((100 * sent) / total);
      const capped = Math.min(95, Math.max(1, Math.floor(rawPct * 0.95)));
      if (capped !== lastShown) {
        lastShown = capped;
        onUiProgressPct?.(capped);
      }
    };

    while (sent < total) {
      maybeAbort();
      const end = Math.min(total, sent + pay);
      const chunk = firmwareBytes.subarray(sent, end);
      await writeMaybeWithResponse(data, chunk, !!wrr);
      sent = end;

      updateUiPct();
      await sleep(paceMs);
    }

    onUiStatus?.("Finishing the update…");
    onUiProgressPct?.(95);
    startCreep();

    await sleep(600);

    console.log("[OTA] transfer done? sent/total =", sent, "/", total);

    console.log("[OTA] CTRL props", {
      write: !!ctrl.properties?.write,
      writeWithoutResponse: !!ctrl.properties?.writeWithoutResponse,
      notify: !!ctrl.properties?.notify,
    });

    await sleep(5000);
    console.log("[OTA] FINI about to send");
    try {
      await writeMaybeWithResponse(ctrl, bytesFromAscii("FINI"), false);
      console.log("[OTA] FINI write returned");
    } catch (e) {
      console.log("[OTA] FINI write FAILED", e);
    }

    const deadline = performance.now() + 15000;
    while (performance.now() < deadline && !done) {
      maybeAbort();
      await sleep(200);
    }

    if (!sawRebootMsg) {
      stopCreep();
      onUiStatus?.("Restarting the device…");
      onUiProgressPct?.(98);
      await sleep(600);
    }

    onUiStatus?.("Done.");
    onUiProgressPct?.(100);
    return;
  } finally {
    stopCreep();
    await stopStat();
  }
}

/**
 * Assign serial: write ID=<serial> and wait for OK on status notify
 */
async function assignSerial({ server, targetSerial, abortSignal, onUiStatus }) {
  if (!server?.connected) throw new Error("Not connected to device");
  if (!targetSerial?.startsWith(LH_PREFIX)) throw new Error("Serial number looks invalid");

  const writeCh = await getCharacteristic(server, DEVSET_SERVICE_UUID, DEVSET_WRITE_UUID);
  const statusCh = await getCharacteristic(server, DEVSET_SERVICE_UUID, DEVSET_STATUS_UUID);

  let statusMsg = null;

  const stop = await startNotifications(statusCh, (u8) => {
    const s = new TextDecoder("utf-8").decode(u8).trim();
    if (!s) return;
    statusMsg = s;
  });

  const maybeAbort = () => {
    if (abortSignal?.aborted) throw new Error("Aborted");
  };

  try {
    onUiStatus?.("Assigning the serial number…");

    const cmd = `ID=${targetSerial}`;
    await writeMaybeWithResponse(writeCh, bytesFromAscii(cmd), false);

    const deadline = performance.now() + 3000;
    while (performance.now() < deadline && !statusMsg) {
      maybeAbort();
      await sleep(100);
    }

    if (!statusMsg) throw new Error("No response from the device");
    if (statusMsg !== "OK") throw new Error("The device could not accept the serial number");

    onUiStatus?.("Serial number saved. Restarting the device…");
    return;
  } finally {
    await stop();
  }
}

function StepCard({ index, title, active, done, children }) {
  const border = active ? "2px solid #111" : "1px solid #ddd";
  const bg = active ? "#fafafa" : "#fff";
  const badgeBg = done ? "#111" : active ? "#111" : "#ddd";
  const badgeColor = done || active ? "#fff" : "#333";

  return (
    <div style={{ padding: 14, border, borderRadius: 12, background: bg }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 999,
            background: badgeBg,
            color: badgeColor,
            display: "grid",
            placeItems: "center",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {done ? "✓" : index}
        </div>
        <div style={{ fontWeight: 700 }}>{title}</div>
      </div>
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

export default function FirmwareUpdateFlow({ onExit, onGoPersonalise }) {
  const [supported] = useState(supportsWebBluetooth());
  const [devFirmwareEnabled] = useState(hasDevFirmwareParam);
  const firmwareUrl = devFirmwareEnabled ? DEV_FIRMWARE_URL : FIRMWARE_URL;
  const targetFwId = devFirmwareEnabled ? DEV_TARGET_FW_ID : TARGET_FW_ID;

  // App settings
  const [pktsz] = useState(() => Number(localStorage.getItem(LS_KEYS.pktsz) || 80));
  const [wrr] = useState(() => localStorage.getItem(LS_KEYS.wrr) === "true");

  // Firmware bytes
  const [firmwareBytes, setFirmwareBytes] = useState(null);

  // Device handles
  const [deviceForUpdate, setDeviceForUpdate] = useState(null);
  const [deviceForProvision, setDeviceForProvision] = useState(null);

  // Serial (initially from selected device name)
  const [targetSerial, setTargetSerial] = useState(() => localStorage.getItem(LS_KEYS.lastSerial) || "");

  // Flow flags
  const [needsSerialAssign, setNeedsSerialAssign] = useState(false);
  const [devInfo, setDevInfo] = useState(null); // { fw, fw_id, provisioned, ... }
  const [updateOutcome, setUpdateOutcome] = useState("unknown");  // "unknown" | "already_up_to_date" | "updated" | "migrated"


  // UI flow state
  // 1: Prepare & select
  // 2: Check / Update
  // 3: Connect again (only if needsSerialAssign)
  // 4: Assign serial (only if needsSerialAssign)
  // 5: Complete
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const [uiStatus, setUiStatus] = useState("");
  const [uiProgress, setUiProgress] = useState(0);
  const [step2Started, setStep2Started] = useState(false);

  const [dockConfirmed, setDockConfirmed] = useState(false);
  const [error, setError] = useState("");

  const abortCtrlRef = useRef(null);

  // Load firmware once
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(firmwareUrl, { cache: "no-store" });
        if (!res.ok) throw new Error("Could not load the firmware file");
        const buf = await res.arrayBuffer();
        if (!alive) return;
        setFirmwareBytes(new Uint8Array(buf));
      } catch {
        if (!alive) return;
        setError("Firmware file could not be loaded. Please contact the LYEONS team.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [firmwareUrl]);

  useEffect(() => {
    if (targetSerial) localStorage.setItem(LS_KEYS.lastSerial, targetSerial);
  }, [targetSerial]);

  const showError = useCallback((msg) => {
    setError(msg);
    setBusy(false);
    abortCtrlRef.current = null;
  }, []);

  const canProceedStep1 = supported && firmwareBytes?.length && dockConfirmed && !busy;
  const canProceedStep2 = supported && step === 2 && deviceForUpdate && !busy;
  const canProceedStep3 = supported && step === 3 && !busy;
  const canProceedStep4 = supported && step === 4 && deviceForProvision && !busy;

  // Step 1: select device
  const selectDevice = useCallback(async () => {
    setError("");
    setBusy(true);
    setUiStatus("");
    setUiProgress(0);
    setDevInfo(null);
    setNeedsSerialAssign(false);

    try {
      const dev = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: "LH-" }],
        // IMPORTANT: include both. If DEVSET exists, we want to access it immediately.
        optionalServices: [OTA_SERVICE_UUID, DEVSET_SERVICE_UUID],
      });

      const nm = (dev?.name || "").trim();
      if (!nm) throw new Error("The selected device has no name.");
      if (!nm.startsWith(LH_PREFIX)) throw new Error("That does not look like a LYEONS device.");

      setTargetSerial(nm);
      setDeviceForUpdate(dev);

      setUiStatus("Device selected.");
      setStep(2);
    } catch {
      showError(
        "Something went wrong while selecting the device. Please try again or if it keeps happening, please contact the LYEONS team."
      );
    } finally {
      setBusy(false);
    }
  }, [showError]);

  // Step 2: connect, detect DEVSET, decide path, and run OTA if needed
  const checkAndUpdate = useCallback(async () => {
    setError("");
    if (!deviceForUpdate) return;
    if (!firmwareBytes?.length) return;

    setStep2Started(true);
    setBusy(true);
    setUiProgress(0);
    setUiStatus("Connecting…");
    setDevInfo(null);
    setNeedsSerialAssign(false);

    const abortCtrl = new AbortController();
    abortCtrlRef.current = abortCtrl;

    try {
      await debugSleep(1000, "before connectGatt");
      const server = await connectGatt(deviceForUpdate, () => {
        // Expected during reboot; ignore here.
      });


      setUiStatus("Checking the device…");
      setUiProgress(2);

      await debugSleep(2000, "before hasDevsetService");
      const devsetPresent = await hasDevsetService(server);

      if (devsetPresent) {
        // Already migrated: read fw_id and decide if OTA is needed
        let info = null;
        try {
          await debugSleep(2000, "before readDevsetInfo");
          info = await readDevsetInfo(server);
          setDevInfo(info);
        } catch {
          // If info read fails, safest is to fall back to OTA-only rather than full migration
          info = { fw: "", fw_id: -1, provisioned: 0 };
          setDevInfo(info);
        }

        const currentFwId = Number(info?.fw_id ?? -1);

        if (currentFwId >= targetFwId) {
          setUiStatus("This device is already up to date.");
          setUiProgress(100);
          await disconnectDevice(deviceForUpdate);
          setUpdateOutcome("already_up_to_date");
          setStep(5);
          return;
        }

        // OTA only
        setUiStatus("Update required. Starting firmware update…");
        setUiProgress(1);

        await sleep(500);

        await firmwareUpdateUpload({
          server,
          firmwareBytes,
          wrr,
          initialChunkSize: pktsz,
          abortSignal: abortCtrl.signal,
          onUiProgressPct: setUiProgress,
          onUiStatus: setUiStatus,
        });

        await disconnectDevice(deviceForUpdate);
        setUiProgress(100);
        setUpdateOutcome("updated");
        setStep(5);
        setUiStatus("The firmware update has completed successfully.");
        return;
      }

      // DEVSET not present => full migration flow (OTA then serial assignment)
      setNeedsSerialAssign(true);

      setUiStatus("Migration required. Starting firmware update…");
      await firmwareUpdateUpload({
        server,
        firmwareBytes,
        wrr,
        initialChunkSize: pktsz,
        abortSignal: abortCtrl.signal,
        onUiProgressPct: setUiProgress,
        onUiStatus: setUiStatus,
      });

      // After update completes, device restarts and should become LH-UNPROVISIONED
      await disconnectDevice(deviceForUpdate);

      setUpdateOutcome("migrated");
      setStep(3);
      setUiStatus(`Next: Please select the LYEONS Heart device again to assign the serial number.`);
    } catch (e){
      showError("The firmware update did not complete successfully. Please contact the LYEONS team for support.");
      console.error("[OTA] error in checkAndUpdate:", e);
    } finally {
      abortCtrlRef.current = null;
      setBusy(false);
    }
  }, [deviceForUpdate, firmwareBytes, pktsz, showError, targetFwId, wrr]);

  // Step 3: select unprovisioned device (only if full migration)
  const selectUnprovisioned = useCallback(async () => {
    setError("");
    setBusy(true);
    setUiStatus("");

    try {
      const dev = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: "LH-" }],
        optionalServices: [DEVSET_SERVICE_UUID],
      });

      setDeviceForProvision(dev);
      setUiStatus("Device selected.");
      setStep(4);
    } catch {
      showError(
        "Something went wrong while selecting the device. Please try again, or if it keeps happening, please contact the LYEONS team."
      );
    } finally {
      setBusy(false);
    }
  }, [showError]);

  // Step 4: assign serial (only if full migration)
  const assign = useCallback(async () => {
    setError("");
    if (!deviceForProvision) return;
    if (!targetSerial) return;

    setBusy(true);
    setUiStatus("Connecting…");

    const abortCtrl = new AbortController();
    abortCtrlRef.current = abortCtrl;

    try {
      const server = await connectGatt(deviceForProvision);
      debugSleep(2000, "after connectGatt in assignSerial");

      await assignSerial({
        server,
        targetSerial,
        abortSignal: abortCtrl.signal,
        onUiStatus: setUiStatus,
      });

      debugSleep(2000, "after assignSerial");

      setUiStatus("Restarting the device…");
      await disconnectDevice(deviceForProvision);

      setUiProgress(100);
      setStep(5);
      setUiStatus("The firmware update has completed successfully.");
    } catch {
      showError("The serial number could not be assigned. Please contact the LYEONS team for support.");
    } finally {
      abortCtrlRef.current = null;
      setBusy(false);
    }
  }, [deviceForProvision, targetSerial, showError]);

  const doneStep1 = step > 1;
  const doneStep2 = step > 2 || step === 5;
  const doneStep3 = step > 3 || step === 5;
  const doneStep4 = step > 4 || step === 5;

  const completeTitle =
  updateOutcome === "already_up_to_date" ? "Device is up to date" : "Update complete";

  const completeLead =
    updateOutcome === "already_up_to_date"
      ? "Your LYEONS Heart is already up to date."
      : "The firmware update has successfully completed :)";

  const completeSub =
    updateOutcome === "already_up_to_date"
      ? "No changes were needed, and the device is ready for use."
      : "Your LYEONS Heart is now up to date and ready for use.";


  return (
    <div style={{ maxWidth: 980, margin: "24px auto", padding: 16, fontFamily: "system-ui" }}>
      <h2 style={{ margin: 0, maxWidth: "none" }}>FIRMWARE UPDATE</h2>
      <div style={{ marginTop: 8, opacity: 0.85 }}>
        This page will check your LYEONS Heart firmware and update it if needed.
        <br />
        Please keep the <b>device charging on the dock</b> and <b>near your computer</b> until the process is complete.
      </div>

      {!supported && (
        <div style={{ marginTop: 12, padding: 12, border: "1px solid #ccc", borderRadius: 12 }}>
          This browser doesn’t support connecting to the device. Please use Chrome or Edge on a laptop/desktop.
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, padding: 14, border: "1px solid #ffb3b3", background: "#fff5f5", borderRadius: 12 }}>
          <div style={{ fontWeight: 700, color: "#a40000" }}>Something went wrong</div>
          <div style={{ marginTop: 6 }}>{error}</div>
        </div>
      )}

      <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <StepCard index={1} title="Prepare and connect" active={step === 1} done={doneStep1}>
          <div style={{ opacity: 0.9 }}>
            Please place the LYEONS Heart on the dock and check it shows a breathing orange or green light.
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
            <input
              type="checkbox"
              checked={dockConfirmed}
              onChange={(e) => setDockConfirmed(e.target.checked)}
              disabled={busy}
            />
            <span>I can see the charging light</span>
          </label>

          <button onClick={selectDevice} disabled={!canProceedStep1} style={{ marginTop: 12, padding: "10px 14px" }}>
            Select LYEONS Heart
          </button>

          <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>
            Selected device: <b>{deviceForUpdate?.name || "—"}</b>
          </div>
        </StepCard>

        <StepCard index={2} title="Check and update" active={step === 2} done={doneStep2}>
          <div style={{ opacity: 0.9 }}>
            The app will check whether an update is needed. An update may take a few minutes.
          </div>

          {!step2Started && (
            <button
              onClick={checkAndUpdate}
              disabled={!canProceedStep2}
              style={{ marginTop: 12, padding: "10px 14px" }}
            >
              Continue
            </button>
          )}

          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
              <div style={{ fontWeight: 700 }}>{uiProgress}%</div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>{uiStatus || " "}</div>
            </div>

            <div style={{ height: 10, background: "#eee", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
              <div
                style={{
                  width: `${Math.max(0, Math.min(100, uiProgress))}%`,
                  height: "100%",
                  background: "#111",
                }}
              />
            </div>
          </div>

          {devInfo && (
            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
              Detected firmware: <b>{devInfo.fw || "—"}</b> (ID <b>{Number.isFinite(devInfo.fw_id) ? devInfo.fw_id : "—"}</b>)
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
            During an update, the breathing light may turn purple and the device may restart automatically.
          </div>
        </StepCard>

        {needsSerialAssign && (
          <StepCard index={3} title="Connect again" active={step === 3} done={doneStep3}>
            <div style={{ opacity: 0.9 }}>
              The device may appear as <b>{UNPROVISIONED_NAME}</b> or <b>{targetSerial}</b>. Please select it to continue.
            </div>

            <button onClick={selectUnprovisioned} disabled={!canProceedStep3} style={{ marginTop: 12, padding: "10px 14px" }}>
              Select device
            </button>

            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>{step < 3 ? " " : uiStatus}</div>
          </StepCard>
        )}

        {needsSerialAssign && (
          <StepCard index={4} title="Assign the serial number" active={step === 4} done={doneStep4}>
            <div style={{ opacity: 0.9 }}>
              This will save the serial number to the device:
              <br /> <b>{targetSerial || "—"}</b>
            </div>

            <button onClick={assign} disabled={!canProceedStep4} style={{ marginTop: 12, padding: "10px 14px" }}>
              Assign serial number
            </button>

            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.9 }}>{step < 4 ? " " : uiStatus}</div>
          </StepCard>
        )}

        {step >= 5 && (
          <div style={{ gridColumn: "span 2" }}>
            <StepCard index={needsSerialAssign ? 5 : 3} title={completeTitle} active={step === 5} done={false}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{completeLead}</div>
              <div style={{ marginTop: 8, opacity: 0.9 }}>{completeSub}</div>

              <div style={{ marginTop: 12, opacity: 0.85 }}>Please feel free to remove the LYEONS Heart from the dock.</div>

              <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "center" }}>
                <button onClick={onExit} style={{ padding: "10px 14px" }}>
                  HOME
                </button>
                <button onClick={onGoPersonalise} style={{ padding: "10px 14px" }}>
                  PERSONALISE YOUR HEART
                </button>
              </div>
            </StepCard>
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 16,
          padding: 14,
          border: "1px solid #ddd",
          borderRadius: 12,
          opacity: 0.9,
        }}
      >
        <div style={{ fontWeight: 700 }}>If something does not look right</div>

        <div style={{ marginTop: 6 }}>
          If the update process fails, or the device is not responding, please contact{" "}
          <a href="mailto:info@lyeons.com" style={{ color: "inherit", textDecoration: "underline", fontWeight: 500 }}>
            info@lyeons.com
          </a>
          .
        </div>
      </div>
    </div>
  );
}
